import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import EventoEntregaSeguridad from './EventoEntregaSeguridad';
import RegistroMovimientoSeguridad from './RegistroMovimientoSeguridad';
import ContratistaSelector from './ContratistaSelector';
import './GestionEntregasSeguridad.css';

export default function GestionEntregasSeguridad({ usuario, contratistaId = null, onBack = null }) {
    const [personal, setPersonal] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [columnFilters, setColumnFilters] = useState({
        contratista: '',
        dni: '',
        cargo: '',
        nombre: '',
        contacto: '',
        sucursal: ''
    });
    const [showEventoModal, setShowEventoModal] = useState(false);
    const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
    const [historialEventos, setHistorialEventos] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [selectedEntregaDetalle, setSelectedEntregaDetalle] = useState(null);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [detalleError, setDetalleError] = useState('');
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [contratistas, setContratistas] = useState([]);
    const [selectedContratistaFilter, setSelectedContratistaFilter] = useState('');
    const [isGlobalMode, setIsGlobalMode] = useState(false);
    const [entregasStats, setEntregasStats] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [showMovimientoModal, setShowMovimientoModal] = useState(false);
    const [movimientoModo, setMovimientoModo] = useState('devolucion');
    const [movimientoEvento, setMovimientoEvento] = useState(null);

    const targetContratistaId = contratistaId || usuario.id_contratista;
    const isSuperadmin = usuario?.rol_nombre === 'superadmin' || (!usuario?.id_contratista && usuario?.tipo !== 'C');

    useEffect(() => {
        const theme = document.documentElement.getAttribute('data-theme') || 'light';
        setIsDarkMode(theme === 'dark');
        const observer = new MutationObserver(() => {
            const newTheme = document.documentElement.getAttribute('data-theme') || 'light';
            setIsDarkMode(newTheme === 'dark');
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isSuperadmin && !contratistaId) {
            setIsGlobalMode(true);
            fetchContratistas();
            fetchPersonal();
        } else if (targetContratistaId) {
            setIsGlobalMode(false);
            fetchPersonal();
        }
    }, [targetContratistaId, isSuperadmin]);

    useEffect(() => {
        fetchStats();
    }, [targetContratistaId, isSuperadmin, selectedContratistaFilter]);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchContratistas = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`);
            if (response.ok) setContratistas(await response.json());
        } catch (err) { console.error(err); }
    };

    const fetchStats = async () => {
        try {
            const params = new URLSearchParams();
            if (isGlobalMode && selectedContratistaFilter) {
                params.append('id_contratista', selectedContratistaFilter);
            }
            const url = `${API_BASE_URL}/api/eventos-entrega-seguridad-stats${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await fetchWithAuth(url);
            if (response.ok) {
                setEntregasStats(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar estadísticas de entregas:', err);
        }
    };

    const fetchPersonal = async () => {
        try {
            setLoading(true);
            const useGlobal = isSuperadmin && !contratistaId;
            const url = useGlobal
                ? `${API_BASE_URL}/api/personal-contratistas`
                : `${API_BASE_URL}/api/personal-contratistas/${targetContratistaId}`;
            const response = await fetchWithAuth(url);

            if (response.ok) {
                const data = await response.json();
                setPersonal(data);
                setError('');
            } else {
                setError('Error al cargar personal');
            }
        } catch (err) {
            console.error('Error al cargar personal:', err);
            setError('Error al cargar personal');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistorialEventos = async (personaId) => {
        try {
            setLoadingHistorial(true);
            const contratistaParam = isGlobalMode && selectedContratistaFilter
                ? `?id_contratista=${selectedContratistaFilter}`
                : '';
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/eventos-entrega-seguridad/personal/${personaId}${contratistaParam}`
            );

            if (response.ok) {
                const data = await response.json();
                setHistorialEventos(data);
            }
        } catch (err) {
            console.error('Error al cargar historial:', err);
        } finally {
            setLoadingHistorial(false);
        }
    };

    const handleNuevoEvento = (persona) => {
        setPersonaSeleccionada(persona);
        setShowEventoModal(true);
    };

    const handleVerHistorial = (persona) => {
        setPersonaSeleccionada(persona);
        fetchHistorialEventos(persona.id);
    };

    const handleVerDetalleEntrega = async (evento) => {
        try {
            setLoadingDetalle(true);
            setDetalleError('');

            const contratistaParam = isGlobalMode && selectedContratistaFilter
                ? `?id_contratista=${selectedContratistaFilter}`
                : '';
            const [eventoResponse, personalResponse, devolucionesResponse, cambiosResponse] = await Promise.all([
                fetchWithAuth(`${API_BASE_URL}/api/eventos-entrega-seguridad/${evento.id}${contratistaParam}`),
                fetchWithAuth(`${API_BASE_URL}/api/personal/${evento.id_personal}`),
                fetchWithAuth(`${API_BASE_URL}/api/eventos-entrega-seguridad/${evento.id}/devoluciones`),
                fetchWithAuth(`${API_BASE_URL}/api/eventos-entrega-seguridad/${evento.id}/cambios`)
            ]);

            if (!eventoResponse.ok) {
                throw new Error('No se pudo cargar el detalle de la entrega');
            }

            if (!personalResponse.ok) {
                throw new Error('No se pudo cargar la información de la persona');
            }

            const detalleEvento = await eventoResponse.json();
            const detallePersonal = await personalResponse.json();
            const devoluciones = devolucionesResponse.ok ? await devolucionesResponse.json() : [];
            const cambios = cambiosResponse.ok ? await cambiosResponse.json() : [];

            setSelectedEntregaDetalle({
                evento: { ...detalleEvento, devoluciones, cambios },
                personal: detallePersonal
            });
        } catch (err) {
            console.error('Error al cargar detalle de entrega:', err);
            setDetalleError(err.message || 'Error al cargar el detalle de la entrega');
        } finally {
            setLoadingDetalle(false);
        }
    };

    const closeDetalleEntrega = () => {
        setSelectedEntregaDetalle(null);
        setDetalleError('');
    };

    const abrirMovimiento = (evento, modo) => {
        setMovimientoEvento(evento);
        setMovimientoModo(modo);
        setShowMovimientoModal(true);
    };

    const cerrarMovimiento = () => {
        setShowMovimientoModal(false);
        setMovimientoEvento(null);
    };

    const recargarDetalle = async (eventoId) => {
        if (!selectedEntregaDetalle) return;
        await handleVerDetalleEntrega({ id: eventoId });
    };

    const normalizeSignatureData = (firma) => {
        if (!firma) return '';
        if (typeof firma !== 'string') return '';

        const value = firma.trim();
        if (!value) return '';

        if (value.startsWith('data:')) return value;

        if (value.startsWith('/9j/')) {
            return `data:image/jpeg;base64,${value}`;
        }

        if (value.startsWith('R0lGOD')) {
            return `data:image/gif;base64,${value}`;
        }

        if (value.startsWith('UklGR')) {
            return `data:image/webp;base64,${value}`;
        }

        return `data:image/png;base64,${value}`;
    };

    const normalizeImageData = (imagen, mimeType = 'image/jpeg') => {
        if (!imagen) return '';
        if (typeof imagen !== 'string') return '';

        const value = imagen.trim();
        if (!value) return '';

        if (value.startsWith('data:')) return value;
        return `data:${mimeType};base64,${value}`;
    };
    const textEncoder = new TextEncoder();

    const createPdfCanvas = (width, height) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        return canvas;
    };

    const loadCanvasImage = (src) => new Promise((resolve) => {
        if (!src) {
            resolve(null);
            return;
        }

        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });

    const drawRoundedRect = (ctx, x, y, w, h, r, fill, stroke) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
        if (fill) {
            ctx.fillStyle = fill;
            ctx.fill();
        }
        if (stroke) {
            ctx.strokeStyle = stroke;
            ctx.stroke();
        }
    };

    const wrapText = (ctx, text, maxWidth) => {
        const words = String(text || '').split(/\s+/).filter(Boolean);
        if (words.length === 0) return [''];

        const lines = [];
        let line = '';
        words.forEach((word) => {
            const testLine = line ? `${line} ${word}` : word;
            if (ctx.measureText(testLine).width > maxWidth && line) {
                lines.push(line);
                line = word;
            } else {
                line = testLine;
            }
        });

        if (line) lines.push(line);
        return lines;
    };

    const drawLabelValue = (ctx, x, y, width, label, value) => {
        ctx.fillStyle = '#64748b';
        ctx.font = '700 18px Arial';
        ctx.fillText(label, x, y);
        ctx.fillStyle = '#0f172a';
        ctx.font = '600 22px Arial';
        const lines = wrapText(ctx, value || '—', width);
        lines.forEach((line, index) => ctx.fillText(line, x, y + 28 + (index * 28)));
        return 28 + (lines.length * 28) + 16;
    };

    const renderPageNumber = (ctx, pageNumber, totalPages, width, height) => {
        ctx.fillStyle = '#64748b';
        ctx.font = '600 18px Arial';
        ctx.textAlign = 'right';
        ctx.fillText(`${pageNumber} de ${totalPages}`, width - 42, height - 34);
        ctx.textAlign = 'left';
    };

    const drawHeader = (ctx, pageWidth, title, subtitle, statusText, fechaEntrega, fechaRegistro) => {
        ctx.fillStyle = '#1e3a8a';
        ctx.font = '800 44px Arial';
        ctx.fillText(title, 64, 86);
        ctx.fillStyle = '#64748b';
        ctx.font = '500 20px Arial';
        ctx.fillText(subtitle, 64, 122);
    };

    const renderPageBackground = (canvas, ctx) => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const renderDeliveryPdfPages = async (detalle) => {
        const evento = detalle.evento;
        const personal = detalle.personal;
        const fechaEntrega = evento?.fecha_entrega ? formatFechaLocal(evento.fecha_entrega) : '—';
        const fechaRegistro = evento?.fecha_creacion ? new Date(evento.fecha_creacion).toLocaleString('es-CL') : '—';
        const fotoPersonal = normalizeImageData(personal?.foto);
        const fotoEntrega = normalizeImageData(evento?.foto_entrega);
        const firmaEntrega = normalizeSignatureData(evento?.firma_entrega);

        const personalImg = await loadCanvasImage(fotoPersonal);
        const entregaImg = await loadCanvasImage(fotoEntrega);
        const firmaImg = await loadCanvasImage(firmaEntrega);

        const pageWidth = 1190;
        const pageHeight = 1684;
        const margin = 64;
        const gap = 18;
        const pages = [];

        const makePage = () => {
            const canvas = createPdfCanvas(pageWidth, pageHeight);
            const ctx = canvas.getContext('2d');
            renderPageBackground(canvas, ctx);
            return { canvas, ctx };
        };

        const drawTitleBar = (ctx, x, y, width, title) => {
            ctx.fillStyle = '#0f172a';
            ctx.font = '800 26px Arial';
            ctx.fillText(title, x, y);
            ctx.fillStyle = '#4f46e5';
            ctx.fillRect(x, y + 14, width, 4);
        };

        const drawImageCard = (ctx, x, y, w, h, title, img, emptyText) => {
            drawRoundedRect(ctx, x, y, w, h, 18, '#f8fafc', '#dbe4ff');
            ctx.fillStyle = '#0f172a';
            ctx.font = '800 22px Arial';
            ctx.fillText(title, x + 18, y + 34);
            const innerX = x + 18;
            const innerY = y + 52;
            const innerW = w - 36;
            const innerH = h - 68;
            if (img) {
                const scale = Math.min(innerW / img.width, innerH / img.height);
                const drawW = img.width * scale;
                const drawH = img.height * scale;
                const dx = innerX + (innerW - drawW) / 2;
                const dy = innerY + (innerH - drawH) / 2;
                ctx.drawImage(img, dx, dy, drawW, drawH);
            } else {
                ctx.fillStyle = '#94a3b8';
                ctx.font = '600 20px Arial';
                ctx.fillText(emptyText, innerX, innerY + 24);
            }
        };

        const drawSignatureCard = (ctx, x, y, w, h) => {
            drawRoundedRect(ctx, x, y, w, h, 18, '#f8fafc', '#dbe4ff');
            ctx.fillStyle = '#0f172a';
            ctx.font = '800 22px Arial';
            ctx.fillText('Firma', x + 18, y + 34);
            const innerX = x + 18;
            const innerY = y + 52;
            const innerW = w - 36;
            const innerH = h - 68;
            drawRoundedRect(ctx, innerX, innerY, innerW, innerH, 16, '#ffffff', '#cbd5e1');
            if (firmaImg) {
                const scale = Math.min(innerW / firmaImg.width, innerH / firmaImg.height);
                const drawW = firmaImg.width * scale;
                const drawH = firmaImg.height * scale;
                const dx = innerX + (innerW - drawW) / 2;
                const dy = innerY + (innerH - drawH) / 2;
                ctx.drawImage(firmaImg, dx, dy, drawW, drawH);
            } else {
                ctx.fillStyle = '#94a3b8';
                ctx.font = '600 20px Arial';
                ctx.fillText('Sin firma registrada', innerX + 18, innerY + 36);
            }
        };

        // Page 1: person data and media
        const rows = (evento?.detalles || []).map((item) => ({
            nombre: item.nombre || '—',
            categoria: item.categoria || '—',
            cantidad: String(item.cantidad || 1),
            talla: item.talla_entregada || '—',
            observaciones: item.observaciones || '—'
        }));
        const measureCanvas = createPdfCanvas(pageWidth, pageHeight);
        const measureCtx = measureCanvas.getContext('2d');
        const tableX = margin + 22;
        const tableW = pageWidth - (margin * 2) - 44;
        const colWidths = [0.22, 0.18, 0.10, 0.12, 0.38].map((ratio) => Math.floor(tableW * ratio));
        const headerH = 44;
        const maxTableBottom = 950;
        const rowPadding = 18;

        const buildRows = () => {
            const chunks = [];
            let current = [];
            let usedHeight = 0;

            rows.forEach((row) => {
                const values = [row.nombre, row.categoria, row.cantidad, row.talla, row.observaciones];
                const wrapped = values.map((value, colIndex) => wrapText(measureCtx, value, colWidths[colIndex] - 24));
                const rowHeight = Math.max(42, ...wrapped.map((lines) => (lines.length * 22) + rowPadding));
                if (current.length > 0 && usedHeight + rowHeight > (maxTableBottom - 250)) {
                    chunks.push(current);
                    current = [];
                    usedHeight = 0;
                }
                current.push({ values, wrapped, rowHeight });
                usedHeight += rowHeight;
            });

            if (current.length > 0 || chunks.length === 0) chunks.push(current);
            return chunks;
        };

        const rowChunks = rows.length > 0 ? buildRows() : [[]];
        const totalPages = rowChunks.length + 1;

        const page1 = makePage();
        const ctx1 = page1.ctx;
        drawHeader(
            ctx1,
            pageWidth,
            'Detalle de Entrega de Artículos',
            `Generado el ${new Date().toLocaleString('es-CL')}`,
            evento?.estado || '—',
            fechaEntrega,
            fechaRegistro
        );

        const infoY = 170;
        drawRoundedRect(ctx1, margin, infoY, pageWidth - (margin * 2), 240, 22, '#ffffff', '#dbe4ff');
        drawTitleBar(ctx1, margin + 22, infoY + 42, 250, 'Datos de la persona');
        const colWidth = (pageWidth - (margin * 2) - 70) / 2;
        const leftX = margin + 22;
        const rightX = leftX + colWidth + 36;
        const baseY = infoY + 92;
        drawLabelValue(ctx1, leftX, baseY, colWidth, 'Nombre completo', `${personal?.nombre || ''} ${personal?.apellido || ''}`.trim());
        drawLabelValue(ctx1, rightX, baseY, colWidth, 'DNI / RUT', personal?.dni || '—');
        drawLabelValue(ctx1, leftX, baseY + 90, colWidth, 'Correo', personal?.email || '—');
        drawLabelValue(ctx1, rightX, baseY + 90, colWidth, 'Cargo', personal?.nombre_cargo || '—');

        const imageTop = 460;
        const imageW = (pageWidth - (margin * 2) - (gap * 2)) / 3;
        const imageH = 320;
        drawImageCard(ctx1, margin, imageTop, imageW, imageH, 'Foto de la persona', personalImg, 'Sin foto registrada');
        drawImageCard(ctx1, margin + imageW + gap, imageTop, imageW, imageH, 'Foto de entrega', entregaImg, 'Sin foto de entrega');
        drawSignatureCard(ctx1, margin + (imageW + gap) * 2, imageTop, imageW, imageH);

        ctx1.fillStyle = '#475569';
        ctx1.font = '600 18px Arial';
        const obsPage1 = wrapText(ctx1, `Observaciones generales: ${evento?.observaciones || '—'}`, pageWidth - (margin * 2));
        obsPage1.forEach((line, index) => ctx1.fillText(line, margin, 824 + (index * 22)));
        ctx1.fillText(`Entregado por: ${evento?.entregado_por_nombre || '—'}`, margin, 890);
        ctx1.fillStyle = '#94a3b8';
        ctx1.font = '600 16px Arial';
        ctx1.fillText('Documento generado automáticamente', margin, pageHeight - 34);
        renderPageNumber(ctx1, 1, totalPages, pageWidth, pageHeight);
        pages.push(page1.canvas);

        // Page 2+ : article table pages
        const headers = ['Artículo', 'Categoría', 'Cantidad', 'Talla', 'Observaciones'];
        rowChunks.forEach((chunk, chunkIndex) => {
            const page = makePage();
            const ctx = page.ctx;
            drawHeader(
                ctx,
                pageWidth,
                chunkIndex === 0 ? 'Artículos entregados' : 'Artículos entregados (continuación)',
                chunkIndex === 0
                    ? `Entrega realizada a ${`${personal?.nombre || ''} ${personal?.apellido || ''}`.trim()}`
                    : 'Continuación del detalle de entrega',
                evento?.estado || '—',
                fechaEntrega,
                fechaRegistro
            );

            drawRoundedRect(ctx, margin, 170, pageWidth - (margin * 2), 850, 22, '#ffffff', '#dbe4ff');
            drawTitleBar(ctx, margin + 22, 212, 250, 'Artículos entregados');

            let y = 248;
            drawRoundedRect(ctx, tableX, y, tableW, headerH, 10, '#eff6ff', '#bfdbfe');
            let cx = tableX;
            headers.forEach((header, index) => {
                ctx.fillStyle = '#1e3a8a';
                ctx.font = '800 18px Arial';
                ctx.fillText(header, cx + 12, y + 28);
                cx += colWidths[index];
            });
            y += headerH;

            if (chunk.length === 0) {
                drawRoundedRect(ctx, tableX, y, tableW, 56, 8, '#fafcff', '#e2e8f0');
                ctx.fillStyle = '#64748b';
                ctx.font = '600 18px Arial';
                ctx.fillText('Sin artículos registrados', tableX + 16, y + 34);
                y += 56;
            } else {
                chunk.forEach((item, index) => {
                    drawRoundedRect(ctx, tableX, y, tableW, item.rowHeight, 8, index % 2 === 0 ? '#ffffff' : '#fafcff', '#e2e8f0');
                    let cellX = tableX;
                    item.wrapped.forEach((lines, colIndex) => {
                        ctx.fillStyle = '#1f2937';
                        ctx.font = colIndex === 0 ? '700 18px Arial' : '600 17px Arial';
                        lines.forEach((line, lineIndex) => {
                            ctx.fillText(line, cellX + 12, y + 24 + (lineIndex * 22));
                        });
                        cellX += colWidths[colIndex];
                    });
                    y += item.rowHeight;
                });
            }

            ctx.fillStyle = '#475569';
            ctx.font = '600 18px Arial';
            ctx.fillText(`Observaciones generales: ${evento?.observaciones || '—'}`, margin + 22, 1040);
            ctx.fillText(`Entregado por: ${evento?.entregado_por_nombre || '—'}`, margin + 22, 1072);
            ctx.fillStyle = '#94a3b8';
            ctx.font = '600 16px Arial';
            ctx.fillText('Documento generado automáticamente', margin, pageHeight - 34);
            renderPageNumber(ctx, chunkIndex + 2, totalPages, pageWidth, pageHeight);
            pages.push(page.canvas);
        });

        return pages;
    };

    const createPdfBlobFromCanvases = async (canvases) => {
        const pageWidthPt = 595;
        const pageHeightPt = 842;
        const pageImages = canvases.map((canvas) => ({
            width: canvas.width,
            height: canvas.height,
            bytes: (() => {
                const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                const base64 = dataUrl.split(',')[1];
                const binary = atob(base64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) {
                    bytes[i] = binary.charCodeAt(i);
                }
                return bytes;
            })()
        }));

        const parts = [];
        let offset = 0;
        const offsets = [0];
        const addPart = (part) => {
            const bytes = typeof part === 'string' ? textEncoder.encode(part) : part;
            parts.push(bytes);
            offset += bytes.length;
        };

        const addObject = (partsList) => {
            offsets.push(offset);
            partsList.forEach((part) => addPart(part));
        };

        addPart(new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34, 0x0a, 0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

        const objects = [];
        const pageKids = [];
        objects.push(['1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n']);

        pageImages.forEach((image, index) => {
            const contentObj = 3 + (index * 3);
            const imageObj = contentObj + 1;
            const pageObj = contentObj + 2;
            pageKids.push(`${pageObj} 0 R`);

            const contentStream = `q\n${pageWidthPt} 0 0 ${pageHeightPt} 0 0 cm\n/Im${index + 1} Do\nQ\n`;
            objects.push([
                `${contentObj} 0 obj\n<< /Length ${textEncoder.encode(contentStream).length} >>\nstream\n${contentStream}endstream\nendobj\n`
            ]);
            objects.push([
                `${imageObj} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.length} >>\nstream\n`,
                image.bytes,
                '\nendstream\nendobj\n'
            ]);
            objects.push([
                `${pageObj} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidthPt} ${pageHeightPt}] /Resources << /XObject << /Im${index + 1} ${imageObj} 0 R >> >> /Contents ${contentObj} 0 R >>\nendobj\n`
            ]);
        });

        objects.push([`2 0 obj\n<< /Type /Pages /Kids [${pageKids.join(' ')}] /Count ${pageKids.length} >>\nendobj\n`]);

        objects.forEach((object) => {
            addObject(object);
        });

        const xrefStart = offset;
        let xref = `xref\n0 ${offsets.length}\n0000000000 65535 f \n`;
        for (let i = 1; i < offsets.length; i += 1) {
            xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
        }
        xref += `trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
        addPart(xref);

        const pdfBytes = new Uint8Array(offset);
        let cursor = 0;
        parts.forEach((part) => {
            pdfBytes.set(part, cursor);
            cursor += part.length;
        });

        return new Blob([pdfBytes], { type: 'application/pdf' });
    };

    const handleGenerarPdfEntrega = async () => {
        if (!selectedEntregaDetalle || generatingPdf) return;

        try {
            setGeneratingPdf(true);
            const pages = await renderDeliveryPdfPages(selectedEntregaDetalle);
            const pdfBlob = await createPdfBlobFromCanvases(pages);
            const downloadUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `detalle-entrega-${selectedEntregaDetalle.evento.id || 'entrega'}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 10000);
        } catch (error) {
            console.error('Error al generar PDF de entrega:', error);
            setDetalleError('No se pudo generar el PDF');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const filteredPersonal = personal.filter(p => {
        const matchesContratista = !isGlobalMode || !selectedContratistaFilter || p.id_contratista === selectedContratistaFilter;
        if (!matchesContratista) return false;

        const f = columnFilters;
        const val = (s) => (s || '').toLowerCase();
        const matches = (field, filter) => !filter || val(field).includes(filter.toLowerCase());

        if (!matches(p.contratista_nombre, f.contratista)) return false;
        if (!matches(p.dni, f.dni)) return false;
        if (!matches(p.nombre_cargo, f.cargo)) return false;
        const nombreCompleto = `${p.nombre || ''} ${p.apellido || ''}`.trim();
        if (!matches(nombreCompleto, f.nombre)) return false;
        const contacto = `${p.email || ''} ${p.telefono || ''}`.trim();
        if (!matches(contacto, f.contacto)) return false;
        if (!matches(p.nombre_sucursal, f.sucursal)) return false;

        return true;
    });

    const formatFechaLocal = (fecha) => {
        if (!fecha) return '—';
        return new Date(String(fecha).slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CL');
    };

    const columnFilterStyle = {
        width: '100%',
        padding: '4px 8px',
        fontSize: '0.8rem',
        border: '1px solid var(--border-light)',
        borderRadius: '4px',
        background: '#fff',
        boxSizing: 'border-box'
    };

    if (loading) {
        return (
            <div className="manager-container gestion-entregas">
                <div className="loading-state">
                    <p>Cargando personal...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manager-container gestion-entregas">
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: '4px' }}>📦 Entrega de Artículos</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Registro y control de entregas, devoluciones y cambios de artículos de seguridad.
                </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            {isGlobalMode && (
                <div style={{ marginBottom: '20px' }}>
                    <ContratistaSelector
                        contratistas={contratistas}
                        value={selectedContratistaFilter}
                        onChange={setSelectedContratistaFilter}
                    />
                </div>
            )}

            {entregasStats && (
                <>
                    <div className="dashboard-grid" style={{ marginBottom: '8px' }}>
                        <div className="stat-card">
                            <div className="stat-icon">📦</div>
                            <div className="stat-info">
                                <h4>Entregas Hoy</h4>
                                <div className="stat-value">{entregasStats.entregas_hoy}</div>
                                <p>Entregas del día</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">📅</div>
                            <div className="stat-info">
                                <h4>Entregas del Mes</h4>
                                <div className="stat-value">{entregasStats.entregas_mes}</div>
                                <p>Mes en curso</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">🗓️</div>
                            <div className="stat-info">
                                <h4>Entregas Mes Anterior</h4>
                                <div className="stat-value">{entregasStats.entregas_mes_anterior}</div>
                                <p>Mes pasado</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">👷</div>
                            <div className="stat-info">
                                <h4>Personas con Entregas</h4>
                                <div className="stat-value">{entregasStats.personas_mes}</div>
                                <p>En el mes actual</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">👥</div>
                            <div className="stat-info">
                                <h4>Personas Mes Anterior</h4>
                                <div className="stat-value">{entregasStats.personas_mes_anterior}</div>
                                <p>Con entregas el mes pasado</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">🦺</div>
                            <div className="stat-info">
                                <h4>Artículos del Mes</h4>
                                <div className="stat-value">{entregasStats.articulos_mes}</div>
                                <p>Unidades entregadas</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">↩️</div>
                            <div className="stat-info">
                                <h4>Devoluciones</h4>
                                <div className="stat-value">{entregasStats.devoluciones_mes}</div>
                                <p>En el mes actual</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">🔄</div>
                            <div className="stat-info">
                                <h4>Cambios</h4>
                                <div className="stat-value">{entregasStats.cambios_mes}</div>
                                <p>En el mes actual</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">📊</div>
                            <div className="stat-info">
                                <h4>Total Entregas</h4>
                                <div className="stat-value">{entregasStats.total_entregas}</div>
                                <p>Histórico acumulado</p>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-card" style={{
                        marginBottom: '20px',
                        padding: '24px',
                        background: isDarkMode ? 'var(--bg-card)' : '#ffffff',
                        borderRadius: '12px',
                        boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                        border: '1px solid var(--border-light)'
                    }}>
                        <h3 style={{
                            marginBottom: '24px',
                            color: isDarkMode ? 'var(--text-main)' : 'var(--sidebar-bg)',
                            fontSize: '1.1rem',
                            fontWeight: '600'
                        }}>
                            📊 Entregas por Día - Últimos 30 días
                        </h3>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={(entregasStats.por_dia || []).map(d => ({
                                ...d,
                                fechaLabel: new Date(String(d.fecha).slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })
                            }))} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                <XAxis
                                    dataKey="fechaLabel"
                                    stroke={isDarkMode ? '#94a3b8' : '#64748b'}
                                    style={{ fontSize: '0.75rem' }}
                                    interval={4}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    stroke={isDarkMode ? '#94a3b8' : '#64748b'}
                                    style={{ fontSize: '0.875rem' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                                        border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        borderRadius: '8px'
                                    }}
                                    labelStyle={{ color: isDarkMode ? '#f1f5f9' : '#0f172a', fontWeight: '600' }}
                                    formatter={(value) => [value, 'Entregas']}
                                />
                                <Bar dataKey="cantidad" fill="#4f46e5" radius={[4, 4, 0, 0]} name="Entregas" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}

            {filteredPersonal.length === 0 ? (
                <div className="empty-state">
                    <p>No se encontraron trabajadores</p>
                </div>
            ) : (
                <div className="delivery-table-wrap">
                    <table className="standard-table delivery-table">
                        <thead>
                            <tr>
                                {isGlobalMode && <th>Contratista</th>}
                                <th>DNI / RUT</th>
                                <th>Cargo</th>
                                <th>Nombre Completo</th>
                                <th>Información de Contacto</th>
                                <th>Sucursal</th>
                                <th>Acciones</th>
                            </tr>
                            <tr>
                                {isGlobalMode && (
                                    <th style={{ padding: '4px 8px' }}>
                                        <input
                                            type="text"
                                            placeholder="Filtrar..."
                                            value={columnFilters.contratista}
                                            onChange={e => setColumnFilters({ ...columnFilters, contratista: e.target.value })}
                                            style={columnFilterStyle}
                                        />
                                    </th>
                                )}
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.dni}
                                        onChange={e => setColumnFilters({ ...columnFilters, dni: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.cargo}
                                        onChange={e => setColumnFilters({ ...columnFilters, cargo: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.nombre}
                                        onChange={e => setColumnFilters({ ...columnFilters, nombre: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.contacto}
                                        onChange={e => setColumnFilters({ ...columnFilters, contacto: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                                <th style={{ padding: '4px 8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Filtrar..."
                                        value={columnFilters.sucursal}
                                        onChange={e => setColumnFilters({ ...columnFilters, sucursal: e.target.value })}
                                        style={columnFilterStyle}
                                    />
                                </th>
                                <th style={{ padding: '4px 8px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPersonal.map(persona => (
                                <tr key={persona.id}>
                                    {isGlobalMode && (
                                        <td data-label="Contratista" style={{ fontWeight: '600', fontSize: '0.85rem' }}>
                                            {persona.contratista_nombre || '—'}
                                        </td>
                                    )}
                                    <td data-label="DNI / RUT" className="delivery-dni">{persona.dni}</td>
                                    <td data-label="Cargo" className="delivery-cargo">{persona.nombre_cargo || '—'}</td>
                                    <td data-label="Nombre Completo" className="delivery-name">
                                        {persona.nombre} {persona.apellido}
                                    </td>
                                    <td data-label="Información de Contacto">
                                        <div className="delivery-contact">
                                            {persona.email ? (
                                                <div className="delivery-contact-line">
                                                    <span className="delivery-contact-icon">✉️</span>
                                                    <span>{persona.email}</span>
                                                </div>
                                            ) : (
                                                <span>—</span>
                                            )}
                                            {persona.telefono && (
                                                <div className="delivery-contact-line">
                                                    <span className="delivery-contact-icon">📞</span>
                                                    <span>{persona.telefono}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td data-label="Sucursal" className="delivery-cargo">{persona.nombre_sucursal || '—'}</td>
                                    <td data-label="Acciones">
                                        <div className="delivery-actions">
                                            <button
                                                className="delivery-btn delivery-btn-primary"
                                                onClick={() => handleNuevoEvento(persona)}
                                                title="Registrar nueva entrega"
                                            >
                                                📦 Nueva Entrega
                                            </button>
                                            <button
                                                className="delivery-btn delivery-btn-secondary"
                                                onClick={() => handleVerHistorial(persona)}
                                                title="Ver historial de entregas"
                                            >
                                                📋 Historial
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL DE NUEVO EVENTO */}
            {showEventoModal && personaSeleccionada && (
                <EventoEntregaSeguridad
                    usuario={usuario}
                    personaId={personaSeleccionada.id}
                    personaNombre={`${personaSeleccionada.nombre} ${personaSeleccionada.apellido}`}
                    contratistaId={isGlobalMode ? (selectedContratistaFilter || personaSeleccionada.id_contratista) : targetContratistaId}
                    empresaId={usuario.empresa_id}
                    onClose={() => {
                        setShowEventoModal(false);
                        setPersonaSeleccionada(null);
                        fetchPersonal();
                    }}
                />
            )}

            {/* MODAL DE HISTORIAL */}
            {personaSeleccionada && !showEventoModal && historialEventos.length >= 0 && (
                <div className="modal-overlay" onClick={() => setPersonaSeleccionada(null)}>
                    <div className="modal-content historial-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>📋 Historial de Entregas</h2>
                            <button className="close-btn" onClick={() => setPersonaSeleccionada(null)}>×</button>
                        </div>

                        <div className="modal-body">
                            <p className="historial-persona">
                                <strong>{personaSeleccionada.nombre} {personaSeleccionada.apellido}</strong> ({personaSeleccionada.dni})
                            </p>

                            {loadingHistorial ? (
                                <p>Cargando historial...</p>
                            ) : historialEventos.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#666' }}>No hay entregas registradas</p>
                            ) : (
                                <div className="historial-lista">
                                    {historialEventos.map(evento => (
                                        <div key={evento.id} className="historial-item">
                                            <div className="item-header">
                                                <span className="item-fecha">
                                                    📅 {formatFechaLocal(evento.fecha_entrega)}
                                                </span>
                                            </div>

                                            <div className="item-detalles">
                                                {evento.entregado_por_nombre && (
                                                    <p><strong>Entregado por:</strong> {evento.entregado_por_nombre}</p>
                                                )}
                                                {evento.observaciones && (
                                                    <p><strong>Observaciones:</strong> {evento.observaciones}</p>
                                                )}
                                            </div>

                                            <div className="item-evidencia">
                                                {evento.foto_entrega && <span className="evidencia-badge">📷 Foto</span>}
                                                {evento.firma_entrega && <span className="evidencia-badge">✍️ Firma</span>}
                                            </div>

                                            <div className="historial-actions">
                                                <button
                                                    className="delivery-btn delivery-btn-primary"
                                                    onClick={() => handleVerDetalleEntrega(evento)}
                                                >
                                                    Ver detalle
                                                </button>
                                                <button
                                                    className="delivery-btn delivery-btn-secondary"
                                                    onClick={() => abrirMovimiento(evento, 'devolucion')}
                                                >
                                                    📦 Devolución
                                                </button>
                                                <button
                                                    className="delivery-btn delivery-btn-secondary"
                                                    onClick={() => abrirMovimiento(evento, 'cambio')}
                                                >
                                                    🔄 Cambio
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setPersonaSeleccionada(null)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedEntregaDetalle && (
                <div className="modal-overlay" onClick={closeDetalleEntrega}>
                    <div className="modal-content detalle-entrega-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>📄 Detalle de Entrega</h2>
                            <button className="close-btn" onClick={closeDetalleEntrega}>×</button>
                        </div>

                        <div className="modal-body detalle-entrega-body">
                            {loadingDetalle ? (
                                <div className="loading-state"><p>Cargando detalle...</p></div>
                            ) : detalleError ? (
                                <div className="error-message">{detalleError}</div>
                            ) : (
                                <>
                                    <div className="detalle-persona-card">
                                        <div className="detalle-persona-info">
                                            <h3>{selectedEntregaDetalle.personal.nombre} {selectedEntregaDetalle.personal.apellido}</h3>
                                            <p><strong>DNI / RUT:</strong> {selectedEntregaDetalle.personal.dni || '—'}</p>
                                            <p><strong>Correo:</strong> {selectedEntregaDetalle.personal.email || '—'}</p>
                                            <p><strong>Cargo:</strong> {selectedEntregaDetalle.personal.nombre_cargo || '—'}</p>
                                            <p><strong>Fecha entrega:</strong> {formatFechaLocal(selectedEntregaDetalle.evento.fecha_entrega)}</p>
                                        </div>
                                    </div>

                                    <div className="detalle-media-grid">
                                        <div className="detalle-media-card">
                                            <h4>Foto de la persona</h4>
                                            {normalizeImageData(selectedEntregaDetalle.personal.foto) ? (
                                                <img src={normalizeImageData(selectedEntregaDetalle.personal.foto)} alt="Foto de la persona" />
                                            ) : (
                                                <p>Sin foto registrada</p>
                                            )}
                                        </div>
                                        <div className="detalle-media-card">
                                            <h4>Foto de entrega</h4>
                                            {normalizeImageData(selectedEntregaDetalle.evento.foto_entrega) ? (
                                                <img src={normalizeImageData(selectedEntregaDetalle.evento.foto_entrega)} alt="Foto de entrega" />
                                            ) : (
                                                <p>Sin foto de entrega</p>
                                            )}
                                        </div>
                                        <div className="detalle-media-card detalle-firma-card">
                                            <h4>Firma</h4>
                                            {normalizeSignatureData(selectedEntregaDetalle.evento.firma_entrega) ? (
                                                <img src={normalizeSignatureData(selectedEntregaDetalle.evento.firma_entrega)} alt="Firma de entrega" />
                                            ) : (
                                                <p>Sin firma registrada</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="detalle-articulos-card">
                                        <h4>Artículos entregados</h4>
                                        <table className="standard-table detalle-articulos-table">
                                            <thead>
                                                <tr>
                                                    <th>Artículo</th>
                                                    <th>Categoría</th>
                                                    <th>Cantidad</th>
                                                    <th>Talla</th>
                                                    <th>Observaciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(selectedEntregaDetalle.evento.detalles || []).map(item => (
                                                    <tr key={item.id || `${item.nombre}-${item.categoria}`}>
                                                        <td data-label="Artículo">{item.nombre || '—'}</td>
                                                        <td data-label="Categoría">{item.categoria || '—'}</td>
                                                        <td data-label="Cantidad">{item.cantidad || 1}</td>
                                                        <td data-label="Talla">{item.talla_entregada || '—'}</td>
                                                        <td data-label="Observaciones">{item.observaciones || '—'}</td>
                                                    </tr>
                                                ))}
                                                {(selectedEntregaDetalle.evento.detalles || []).length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Sin artículos registrados</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="detalle-notes-card">
                                        <p><strong>Observaciones generales:</strong> {selectedEntregaDetalle.evento.observaciones || '—'}</p>
                                        <p><strong>Entregado por:</strong> {selectedEntregaDetalle.evento.entregado_por_nombre || '—'}</p>
                                        <p><strong>Estado:</strong> {selectedEntregaDetalle.evento.estado || '—'}</p>
                                    </div>

                                    {(selectedEntregaDetalle.evento.devoluciones?.length > 0 || selectedEntregaDetalle.evento.cambios?.length > 0) && (
                                        <>
                                            {selectedEntregaDetalle.evento.devoluciones?.length > 0 && (
                                                <div className="detalle-articulos-card">
                                                    <h4>📦 Devoluciones</h4>
                                                    <table className="standard-table detalle-articulos-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Fecha</th>
                                                                <th>Artículo</th>
                                                                <th>Motivo</th>
                                                                <th>Observaciones</th>
                                                                <th>Recibido por</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedEntregaDetalle.evento.devoluciones.map(d => (
                                                                <tr key={d.id}>
                                                                    <td data-label="Fecha">{formatFechaLocal(d.fecha_devolucion)}</td>
                                                                    <td data-label="Artículo">{d.articulo_nombre || '—'}</td>
                                                                    <td data-label="Motivo">{d.motivo || '—'}</td>
                                                                    <td data-label="Observaciones">{d.observaciones || '—'}</td>
                                                                    <td data-label="Recibido por">{d.recibido_por_nombre || '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {selectedEntregaDetalle.evento.cambios?.length > 0 && (
                                                <div className="detalle-articulos-card">
                                                    <h4>🔄 Cambios de Artículo</h4>
                                                    <table className="standard-table detalle-articulos-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Fecha</th>
                                                                <th>Artículo Anterior</th>
                                                                <th>Artículo Nuevo</th>
                                                                <th>Talla Ant.</th>
                                                                <th>Talla Nue.</th>
                                                                <th>Cant.</th>
                                                                <th>Motivo</th>
                                                                <th>Recibido por</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedEntregaDetalle.evento.cambios.map(c => (
                                                                <tr key={c.id}>
                                                                    <td data-label="Fecha">{formatFechaLocal(c.fecha_cambio)}</td>
                                                                    <td data-label="Artículo Anterior">{c.articulo_anterior_nombre || '—'}</td>
                                                                    <td data-label="Artículo Nuevo">{c.articulo_nuevo_nombre || '—'}</td>
                                                                    <td data-label="Talla Ant.">{c.talla_anterior || '—'}</td>
                                                                    <td data-label="Talla Nue.">{c.talla_nueva || '—'}</td>
                                                                    <td data-label="Cant.">{c.cantidad || 1}</td>
                                                                    <td data-label="Motivo">{c.motivo || '—'}</td>
                                                                    <td data-label="Recibido por">{c.recibido_por_nombre || '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <div className="detalle-actions-footer no-print">
                                        <button className="delivery-btn delivery-btn-secondary" onClick={closeDetalleEntrega}>
                                            Cerrar
                                        </button>
                                        <button className="delivery-btn delivery-btn-secondary" onClick={() => abrirMovimiento(selectedEntregaDetalle.evento, 'devolucion')}>
                                            📦 Registrar Devolución
                                        </button>
                                        <button className="delivery-btn delivery-btn-secondary" onClick={() => abrirMovimiento(selectedEntregaDetalle.evento, 'cambio')}>
                                            🔄 Registrar Cambio
                                        </button>
                                        <button className="delivery-btn delivery-btn-secondary" onClick={handleGenerarPdfEntrega}>
                                            Generar PDF
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showMovimientoModal && movimientoEvento && personaSeleccionada && (
                <RegistroMovimientoSeguridad
                    modo={movimientoModo}
                    evento={movimientoEvento}
                    persona={personaSeleccionada}
                    usuario={usuario}
                    empresaId={usuario.empresa_id}
                    onClose={cerrarMovimiento}
                    onSuccess={() => {
                        recargarDetalle(movimientoEvento.id);
                        if (personaSeleccionada) fetchHistorialEventos(personaSeleccionada.id);
                    }}
                />
            )}

        </div>
    );
}
